import React, { useEffect, useState, useRef } from 'react';

interface Props {
  selectedVersion: string;
  versions: string[];
  position?: string;
}

const VersionsDropdown = ({ selectedVersion, versions, position }: Props) => {
  const [currentVersion] = useState({
    url: `/${selectedVersion}`,
    displayVersion: `v${selectedVersion.split('/')[1]}`,
  });
  const [showDropdown, setShowDropdown] = useState(false);
  const [enableFilter, setEnableFilter] = useState(false);
  const [dropdownVersions, setDropdownVersions] = useState(
    versions.filter((v) => v !== currentVersion.url),
  );
  const [filterText, setFilterText] = useState('');
  const filterInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setShowDropdown(false);
    setEnableFilter(false);
    setFilterText('');
  }, [position]);

  useEffect(() => {
    if (enableFilter && filterInputRef.current) {
      filterInputRef.current.focus();
    }
  }, [enableFilter]);

  // handle dropdown list when start filter
  useEffect(() => {
    const filtered = versions.filter((v) => {
      const versionName = `v${v.split('/')[2]}`;
      return versionName.toLowerCase().includes(filterText.toLowerCase());
    });
    setDropdownVersions(filtered);
  }, [filterText]);

  useEffect(() => {
    if (enableFilter) {
      setDropdownVersions(versions);
    } else {
      initDropdownVersions();
    }
  }, [enableFilter]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.tile-versions')) {
        setShowDropdown(false);
        setEnableFilter(false);
        setFilterText('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const initDropdownVersions = () => {
    setDropdownVersions(versions.filter((v) => v !== currentVersion.url));
    setFilterText('');
  };
  const goToVersion = (url: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.href = url;
  };
  const changeFilterState = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEnableFilter(!enableFilter);
    initDropdownVersions();
  };
  const handleKeyDown = (e: React.KeyboardEvent, url?: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (url) {
        goToVersion(url, e as unknown as React.MouseEvent);
      } else {
        handleState(e as unknown as React.MouseEvent);
      }
    }
  };
  const changeDropdownState = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdown(!showDropdown);

    if (!showDropdown) {
      setEnableFilter(false);
      setFilterText('');
    }
  };
  const handleState = (e: React.MouseEvent) => {
    if (!showDropdown) {
      changeDropdownState(e);
      return;
    }

    changeFilterState(e);
  };

  return (
    <>
      {versions.length === 1 && (
        <span data-test-id="single-version">
          {currentVersion.displayVersion} (latest)
        </span>
      )}
      {versions.length > 1 && (
        <button
          className="tile-versions hide-mobile"
          tabIndex={0}
          onClick={handleState}
          onKeyDown={(e) => handleKeyDown(e)}
        >
          <div className="change-versions" data-test-id="change-versions">
            {!enableFilter && (
              <div className="tile-current-ver" data-test-id="tile-current-ver">
                {currentVersion.displayVersion}
              </div>
            )}
            {enableFilter && (
              <input
                ref={filterInputRef}
                type="text"
                className="version-filter-input"
                data-test-id="version-filter-input"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            )}
            <svg
              data-test-id="version-caret"
              onClick={changeDropdownState}
              className={`toggle-dropdown ${showDropdown ? 'rotated' : ''}`}
            >
              <use
                href="/assets/images/icons/fa-chevron-right.svg#fa-chevron-right"
                fill="currentColor"
              ></use>
            </svg>
          </div>
          {showDropdown && (
            <ul className="tile-dropdown" data-test-id="tile-dropdown">
              {dropdownVersions.map((v, i) => {
                const versionName = v.split('/')[2];
                const displayVersion = `v${versionName}`;
                const url = v.replace(' (latest)', '');

                return (
                  <li
                    className="tile-dropdown-item"
                    key={`tile-dropdown-item-${versionName}`}
                  >
                    <button
                      data-test-id={`version-item-${versionName}`}
                      onClick={(e) => goToVersion(url, e)}
                      onKeyDown={(e) => handleKeyDown(e, url)}
                      type="button"
                    >
                      {displayVersion}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </button>
      )}
    </>
  );
};

export default VersionsDropdown;
